import { env } from "@/env";

import { readCookie } from "./cookies";
import { ApiError, toErrorCode, type ErrorDetails } from "./errors";

/**
 * Cliente HTTP único da aplicação.
 *
 * Regras do contrato do backend (docs/api.md e docs/security.md):
 *
 * 1. `credentials: "include"` em TODA requisição — a autenticação é por
 *    cookie de sessão HttpOnly. Não existe token em localStorage.
 * 2. Requisições que mudam estado (POST/PUT/PATCH/DELETE) enviam o header
 *    `X-CSRF-Token`, cujo valor vem do cookie legível `ponto_certo_csrf`.
 * 3. Operações financeiras aceitam `Idempotency-Key`; o mesmo valor deve ser
 *    reenviado no retry.
 * 4. Toda falha vira `ApiError`, com o `code` do backend preservado.
 */

const CSRF_COOKIE = "ponto_certo_csrf";
const CSRF_HEADER = "X-CSRF-Token";
const IDEMPOTENCY_HEADER = "Idempotency-Key";
const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestOptions {
  method?: HttpMethod;
  /** Corpo JSON. Serializado automaticamente. */
  body?: unknown;
  /** Query string. Valores nulos/vazios são omitidos. */
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Chave de idempotência para endpoints financeiros. */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

/**
 * Assinantes de 401. O AuthProvider se inscreve para limpar a sessão e
 * redirecionar — sem acoplar o cliente HTTP ao React Router.
 */
type UnauthenticatedListener = () => void;
const unauthenticatedListeners = new Set<UnauthenticatedListener>();

export function onUnauthenticated(listener: UnauthenticatedListener): () => void {
  unauthenticatedListeners.add(listener);
  return () => {
    unauthenticatedListeners.delete(listener);
  };
}

/** Endpoints onde 401 é resposta esperada e NÃO significa sessão perdida. */
const SILENT_401_PATHS = ["/api/v1/auth/login", "/api/v1/platform/auth/login"];

function buildUrl(path: string, query: RequestOptions["query"]): string {
  const base = `${env.apiUrl}${path}`;
  if (!query) return base;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === "") continue;
    params.set(key, String(value));
  }

  const serialized = params.toString();
  return serialized ? `${base}?${serialized}` : base;
}

function buildHeaders(options: RequestOptions, method: HttpMethod): Headers {
  const headers = new Headers({ Accept: "application/json" });

  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }

  if (UNSAFE_METHODS.has(method)) {
    const csrfToken = readCookie(CSRF_COOKIE);
    // Sem token o backend responde 403. Não silenciamos nem contornamos:
    // a ausência significa que não há sessão válida.
    if (csrfToken) headers.set(CSRF_HEADER, csrfToken);
  }

  if (options.idempotencyKey) {
    headers.set(IDEMPOTENCY_HEADER, options.idempotencyKey);
  }

  return headers;
}

function parseErrorPayload(payload: unknown): {
  code: string;
  message: string;
  details: ErrorDetails;
  requestId: string | null;
} | null {
  if (typeof payload !== "object" || payload === null) return null;

  const envelope = (payload as { error?: unknown }).error;
  if (typeof envelope !== "object" || envelope === null) return null;

  const record = envelope as Record<string, unknown>;
  if (typeof record["code"] !== "string") return null;

  return {
    code: record["code"],
    message: typeof record["message"] === "string" ? record["message"] : "",
    details:
      typeof record["details"] === "object" && record["details"] !== null
        ? (record["details"] as ErrorDetails)
        : {},
    requestId:
      typeof record["request_id"] === "string" ? record["request_id"] : null,
  };
}

function notifyUnauthenticated(): void {
  for (const listener of unauthenticatedListeners) listener();
}

export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const method = options.method ?? "GET";
  const url = buildUrl(path, options.query);

  const init: RequestInit = {
    method,
    // Obrigatório: sem isto o navegador não envia o cookie de sessão.
    credentials: "include",
    headers: buildHeaders(options, method),
  };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);
  if (options.signal) init.signal = options.signal;

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;

    throw new ApiError({
      code: "network_error",
      status: 0,
      message: "Falha de rede ao contatar a API.",
    });
  }

  if (response.status === 204 || response.status === 205) {
    return undefined as T;
  }

  const rawBody = await response.text();
  let payload: unknown = null;
  if (rawBody.length > 0) {
    try {
      payload = JSON.parse(rawBody) as unknown;
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const parsed = parseErrorPayload(payload);

    if (response.status === 401 && !SILENT_401_PATHS.includes(path)) {
      notifyUnauthenticated();
    }

    if (parsed) {
      throw new ApiError({
        code: toErrorCode(parsed.code),
        status: response.status,
        message: parsed.message,
        details: parsed.details,
        requestId: parsed.requestId,
      });
    }

    throw new ApiError({
      code: "internal_error",
      status: response.status,
      message: `Resposta inesperada da API (HTTP ${response.status}).`,
      requestId: response.headers.get("X-Request-Id"),
    });
  }

  if (payload === null && rawBody.length > 0) {
    throw new ApiError({
      code: "malformed_response",
      status: response.status,
      message: "A resposta da API não é um JSON válido.",
      requestId: response.headers.get("X-Request-Id"),
    });
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, options: Omit<RequestOptions, "method" | "body"> = {}) =>
    request<T>(path, { ...options, method: "GET" }),

  post: <T>(path: string, options: Omit<RequestOptions, "method"> = {}) =>
    request<T>(path, { ...options, method: "POST" }),

  patch: <T>(path: string, options: Omit<RequestOptions, "method"> = {}) =>
    request<T>(path, { ...options, method: "PATCH" }),

  delete: <T>(path: string, options: Omit<RequestOptions, "method"> = {}) =>
    request<T>(path, { ...options, method: "DELETE" }),
};

/** Chave de idempotência para uma operação financeira. Reutilize no retry. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
